import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';

export function useSubscriptionStatus() {
  const [subscriptionData, setSubscriptionData] = useState({
    isSubscribed: false,
    isLoading: true,
    error: null,
    subscriptionStatus: null,
    isPro: false,
    subscriptionId: null,
    currentPeriodEnd: null,
    lastChecked: null,
    planType: null,
    stripeCustomerId: null
  });

  const checkSubscriptionStatus = useCallback(async (userId) => {
    if (!userId) {
      setSubscriptionData(prev => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
        error: null,
        subscriptionStatus: 'no_user'
      }));
      return;
    }

    try {
      setSubscriptionData(prev => ({ ...prev, isLoading: true, error: null }));

      // Check Firestore for cached subscription data
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        setSubscriptionData(prev => ({
          ...prev,
          isSubscribed: false,
          isLoading: false,
          subscriptionStatus: 'no_profile',
          lastChecked: new Date().toISOString()
        }));
        return;
      }

      const userData = userDoc.data();
      const {
        isSessionsPro = false,
        isSessionsTrialing = false,
        sessionsSubscriptionStatus = null,
        sessionsSubscriptionId = null,
        sessionsCurrentPeriodEnd = null,
        sessionsCurrentPeriodStart = null,
        sessionsTrialEnd = null,
        sessionsStripeCustomerId = null,
        sessionsSubscriptionCreatedAt = null,
        sessionsLastUpdated = null
      } = userData;

      // Use Sessions-specific field names
      const finalSubscriptionStatus = sessionsSubscriptionStatus || 'none';
      const finalSubscriptionId = sessionsSubscriptionId;
      const finalCurrentPeriodEnd = sessionsCurrentPeriodEnd;
      const finalStripeCustomerId = sessionsStripeCustomerId;

      // Use Sessions subscription logic - check both isSessionsPro and isSessionsTrialing
      const hasActiveSubscription = isSessionsPro || ['active', 'trialing'].includes(finalSubscriptionStatus);
      
      // Check if subscription has expired (using timestamp format like Klorah)
      let subscriptionExpired = false;
      if (finalCurrentPeriodEnd) {
        const periodEndDate = new Date(finalCurrentPeriodEnd * 1000); // Klorah uses timestamps
        subscriptionExpired = periodEndDate < new Date();
      }

      // Final subscription status using Klorah's logic
      const finalIsSubscribed = hasActiveSubscription && !subscriptionExpired;

      setSubscriptionData({
        isSubscribed: finalIsSubscribed,
        isLoading: false,
        error: null,
        subscriptionStatus: finalSubscriptionStatus,
        isPro: isSessionsPro,
        isTrialing: isSessionsTrialing,
        subscriptionId: finalSubscriptionId,
        currentPeriodEnd: finalCurrentPeriodEnd,
        currentPeriodStart: sessionsCurrentPeriodStart,
        trialEnd: sessionsTrialEnd,
        stripeCustomerId: finalStripeCustomerId,
        subscriptionExpired,
        subscriptionCreatedAt: sessionsSubscriptionCreatedAt,
        lastUpdated: sessionsLastUpdated,
        lastChecked: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error checking subscription status:', error);
      setSubscriptionData(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
        lastChecked: new Date().toISOString()
      }));
    }
  }, []);

  const refreshSubscriptionStatus = useCallback(() => {
    const user = auth.currentUser;
    if (user) {
      checkSubscriptionStatus(user.uid);
    }
  }, [checkSubscriptionStatus]);

  // Sync subscription status with backend (manual sync with Stripe)
  const syncWithStripe = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      setSubscriptionData(prev => ({ ...prev, isLoading: true }));
      
      // Call the manual sync endpoint that actively queries Stripe
      const response = await fetch(`https://klorah-fast-server.onrender.com/api/sessions/subscription/sync/${user.uid}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Sync result:', result);
        
        // After syncing, refresh local data to get updated Firebase data
        await checkSubscriptionStatus(user.uid);
        return result;
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to sync with Stripe');
      }
    } catch (error) {
      console.error('Error syncing with Stripe:', error);
      setSubscriptionData(prev => ({
        ...prev,
        error: `Failed to sync subscription status: ${error.message}`,
        isLoading: false
      }));
      throw error; // Re-throw so the caller can handle retries
    }
  }, [checkSubscriptionStatus]);

  // Set up real-time subscription monitoring
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setSubscriptionData(prev => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
        subscriptionStatus: 'no_user'
      }));
      return;
    }

    // Set up real-time listener for user document changes
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        const {
          isSessionsPro = false,
          isSessionsTrialing = false,
          sessionsSubscriptionStatus = null,
          sessionsSubscriptionId = null,
          sessionsCurrentPeriodEnd = null,
          sessionsCurrentPeriodStart = null,
          sessionsTrialEnd = null,
          sessionsStripeCustomerId = null,
          sessionsSubscriptionCreatedAt = null,
          sessionsLastUpdated = null
        } = userData;

        // Use Sessions-specific field names
        const finalSubscriptionStatus = sessionsSubscriptionStatus || 'none';
        const finalSubscriptionId = sessionsSubscriptionId;
        const finalCurrentPeriodEnd = sessionsCurrentPeriodEnd;
        const finalStripeCustomerId = sessionsStripeCustomerId;

        // Use Sessions subscription logic
        const hasActiveSubscription = isSessionsPro || ['active', 'trialing'].includes(finalSubscriptionStatus);
        
        // Check if subscription has expired (using timestamp format like Klorah)
        let subscriptionExpired = false;
        if (finalCurrentPeriodEnd) {
          const periodEndDate = new Date(finalCurrentPeriodEnd * 1000); // Klorah uses timestamps
          subscriptionExpired = periodEndDate < new Date();
        }

        // Final subscription status using Klorah's logic
        const finalIsSubscribed = hasActiveSubscription && !subscriptionExpired;

        setSubscriptionData({
          isSubscribed: finalIsSubscribed,
          isLoading: false,
          error: null,
          subscriptionStatus: finalSubscriptionStatus,
          isPro: isSessionsPro,
          isTrialing: isSessionsTrialing,
          subscriptionId: finalSubscriptionId,
          currentPeriodEnd: finalCurrentPeriodEnd,
          currentPeriodStart: sessionsCurrentPeriodStart,
          trialEnd: sessionsTrialEnd,
          stripeCustomerId: finalStripeCustomerId,
          subscriptionExpired,
          subscriptionCreatedAt: sessionsSubscriptionCreatedAt,
          lastUpdated: sessionsLastUpdated,
          lastChecked: new Date().toISOString()
        });
      } else {
        setSubscriptionData(prev => ({
          ...prev,
          isSubscribed: false,
          isLoading: false,
          subscriptionStatus: 'no_profile'
        }));
      }
    }, (error) => {
      console.error('Error listening to subscription changes:', error);
      setSubscriptionData(prev => ({
        ...prev,
        error: 'Failed to monitor subscription status',
        isLoading: false
      }));
    });

    return () => unsubscribe();
  }, []);

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        checkSubscriptionStatus(user.uid);
      } else {
        setSubscriptionData({
          isSubscribed: false,
          isLoading: false,
          error: null,
          subscriptionStatus: 'no_user',
          isPro: false,
          subscriptionId: null,
          currentPeriodEnd: null,
          planType: null,
          stripeCustomerId: null,
          lastChecked: new Date().toISOString()
        });
      }
    });

    return () => unsubscribe();
  }, [checkSubscriptionStatus]);

  return {
    ...subscriptionData,
    refreshSubscriptionStatus,
    syncWithStripe
  };
} 